import { diffChars } from 'diff';
import { ReactNode } from 'react';
import { validateAnswerEvaluation } from 'utils/answerEvaluation';
import { validateQuestionGeneration } from 'utils/questionGeneration';

import { MediaAssetType } from 'types';
import {
  FIELD_LABELS,
  OPTION_REQUIRED_TYPES,
  QUESTION_TYPE_LABEL_BY_SLUG,
  QUESTION_TYPE_MEDIA_RESTRICTIONS,
  QuestionOptionEditable,
} from './types';
import {
  MAX_SELECTIONS_METADATA_KEY,
  MIN_SELECTIONS_METADATA_KEY,
  parseMultipleSelectLimitValue,
} from '../../../utils/multipleSelectLimits';

export type MultipleSelectLimitInputs = {
  minSelections: string;
  maxSelections: string;
};

export type MultipleSelectLimitValidation = {
  minError?: string;
  maxError?: string;
};

export const stringOrEmpty = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

const sortObject = (input: unknown): unknown => {
  if (Array.isArray(input)) {
    return input.map((item) => sortObject(item));
  }
  if (input && typeof input === 'object') {
    return Object.keys(input as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortObject((input as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }
  return input;
};

export const stableStringify = (value: unknown): string => {
  if (value === undefined) {
    return 'undefined';
  }
  return JSON.stringify(sortObject(value));
};

export const normalizeOptionData = (options?: QuestionOptionEditable[] | null) => {
  if (!options || options.length === 0) {
    return [];
  }

  return options
    .map((option, index) => ({
      order: typeof option.order === 'number' ? option.order : index,
      front_text: stringOrEmpty(option.front_text),
      back_text: stringOrEmpty(option.back_text),
      value:
        typeof option.value === 'number' && Number.isFinite(option.value) ? option.value : index,
      is_correct: !!option.is_correct,
      is_other: !!option.is_other,
    }))
    .sort((a, b) => a.order - b.order)
    .map((option) => ({
      front_text: option.front_text,
      back_text: option.back_text,
      value: option.value,
      is_correct: option.is_correct,
      is_other: option.is_other,
    }));
};

export const getAllowedMediaTypes = (questionType?: string): MediaAssetType[] | undefined => {
  if (!questionType) {
    return undefined;
  }
  const allowed = QUESTION_TYPE_MEDIA_RESTRICTIONS[questionType];
  return allowed && allowed.length > 0 ? allowed : undefined;
};

export const validateOptionLabelsForQuestionType = (
  questionType: string,
  options: QuestionOptionEditable[],
): string | null => {
  if (!OPTION_REQUIRED_TYPES.has(questionType)) {
    return null;
  }

  if (options.length === 0) {
    return 'Add at least one option for this question type.';
  }

  const blankOptionIndex = options.findIndex(
    (option) => typeof option.front_text !== 'string' || option.front_text.trim().length === 0,
  );

  return blankOptionIndex === -1
    ? null
    : `Option ${blankOptionIndex + 1} requires nonblank Front Text.`;
};

export const getMultipleSelectLimitInputsFromMetadata = (
  metadata?: Record<string, unknown> | null,
): MultipleSelectLimitInputs => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return { minSelections: '', maxSelections: '' };
  }

  const minParsed = parseMultipleSelectLimitValue(metadata[MIN_SELECTIONS_METADATA_KEY]);
  const maxParsed = parseMultipleSelectLimitValue(metadata[MAX_SELECTIONS_METADATA_KEY]);

  return {
    minSelections: typeof minParsed.value === 'number' ? String(minParsed.value) : '',
    maxSelections: typeof maxParsed.value === 'number' ? String(maxParsed.value) : '',
  };
};

export const updateMultipleSelectMetadataWithInput = (
  metadata: Record<string, unknown> | undefined,
  field: keyof MultipleSelectLimitInputs,
  rawValue: string,
): Record<string, unknown> => {
  const nextMetadata = { ...(metadata ?? {}) };
  const metadataKey =
    field === 'minSelections' ? MIN_SELECTIONS_METADATA_KEY : MAX_SELECTIONS_METADATA_KEY;
  const parsed = parseMultipleSelectLimitValue(rawValue);

  if (rawValue.trim().length === 0 || parsed.error || typeof parsed.value !== 'number') {
    delete nextMetadata[metadataKey];
    return nextMetadata;
  }

  nextMetadata[metadataKey] = parsed.value;
  return nextMetadata;
};

export const validateMultipleSelectLimitInputs = (
  inputs: MultipleSelectLimitInputs,
  optionCount: number,
): MultipleSelectLimitValidation => {
  const validation: MultipleSelectLimitValidation = {};
  const minParsed = parseMultipleSelectLimitValue(inputs.minSelections);
  const maxParsed = parseMultipleSelectLimitValue(inputs.maxSelections);

  if (inputs.minSelections.trim().length > 0 && minParsed.error) {
    validation.minError = minParsed.error;
  }

  if (inputs.maxSelections.trim().length > 0 && maxParsed.error) {
    validation.maxError = maxParsed.error;
  }

  if (
    typeof minParsed.value === 'number' &&
    typeof maxParsed.value === 'number' &&
    minParsed.value > maxParsed.value
  ) {
    validation.maxError = 'Must be greater than or equal to min selections.';
  }

  if (optionCount > 0) {
    if (typeof minParsed.value === 'number' && minParsed.value > optionCount) {
      validation.minError = `Must be less than or equal to the number of options (${optionCount}).`;
    }
    if (typeof maxParsed.value === 'number' && maxParsed.value > optionCount) {
      validation.maxError = `Must be less than or equal to the number of options (${optionCount}).`;
    }
  }

  return validation;
};

export const sanitizeMetadataForQuestionType = (
  metadata: Record<string, unknown> | undefined,
  questionType: string,
  inputs?: MultipleSelectLimitInputs,
): Record<string, unknown> => {
  const nextMetadata = { ...(metadata ?? {}) };

  if (questionType !== 'multiple_select') {
    delete nextMetadata[MIN_SELECTIONS_METADATA_KEY];
    delete nextMetadata[MAX_SELECTIONS_METADATA_KEY];
    return nextMetadata;
  }

  if (!inputs) {
    return nextMetadata;
  }

  const minParsed = parseMultipleSelectLimitValue(inputs.minSelections);
  const maxParsed = parseMultipleSelectLimitValue(inputs.maxSelections);

  if (typeof minParsed.value === 'number') {
    nextMetadata[MIN_SELECTIONS_METADATA_KEY] = minParsed.value;
  } else {
    delete nextMetadata[MIN_SELECTIONS_METADATA_KEY];
  }

  if (typeof maxParsed.value === 'number') {
    nextMetadata[MAX_SELECTIONS_METADATA_KEY] = maxParsed.value;
  } else {
    delete nextMetadata[MAX_SELECTIONS_METADATA_KEY];
  }

  return nextMetadata;
};

export const validateMetadataForQuestionType = (
  metadata: Record<string, unknown> | undefined,
  questionType: string,
): string | null => {
  const generationError = validateQuestionGeneration(metadata, questionType);
  if (generationError) return generationError;
  const evaluationError = validateAnswerEvaluation(metadata, questionType);
  if (evaluationError) return evaluationError;
  if (questionType === 'roleplay') {
    const requiredStringKeys = ['persona_key', 'actor_prompt', 'win_condition_prompt'] as const;
    for (const key of requiredStringKeys) {
      if (typeof metadata?.[key] !== 'string' || !String(metadata[key]).trim()) {
        return `Roleplay requires a nonblank "${key}" metadata value.`;
      }
    }
    const objectives = metadata?.learning_objectives;
    if (!Array.isArray(objectives) || !objectives.some((value) => String(value).trim())) {
      return 'Roleplay requires at least one learning objective.';
    }
    const duration = Number(metadata?.max_active_seconds);
    if (!Number.isInteger(duration) || duration < 60 || duration > 3600) {
      return 'Roleplay duration must be a whole number between 60 and 3600 seconds.';
    }
    const turns = Number(metadata?.max_learner_turns);
    if (!Number.isInteger(turns) || turns < 1 || turns > 50) {
      return 'Roleplay turn limit must be a whole number between 1 and 50.';
    }
    return null;
  }

  if (questionType !== 'callback_summary') {
    return null;
  }

  const customUserPrompt = metadata?.custom_user_prompt;
  const legacyTemplate = metadata?.template;
  const hasUserPrompt =
    (typeof customUserPrompt === 'string' && customUserPrompt.trim().length > 0) ||
    (typeof legacyTemplate === 'string' && legacyTemplate.trim().length > 0);

  return hasUserPrompt
    ? null
    : 'Callback Summary requires a nonblank "custom_user_prompt" metadata value.';
};

const TEXT_DIFF_FIELDS = new Set<string>(['text', 'header', 'subtext', 'low_text', 'high_text']);

const renderStringDiff = (
  left: string,
  right: string,
  variant: 'before' | 'after',
): ReactNode => {
  const parts = diffChars(left, right);
  const accessibleText = variant === 'before' ? left : null;
  return (
    <span className="font-mono whitespace-pre-wrap break-words">
      {accessibleText ? <span className="sr-only">{accessibleText}</span> : null}
      "
      {parts.map((part, index) => {
        if (!part.value) {
          return null;
        }

        const key = `${index}-${part.value.length}`;
        const base = part.value;

        if (part.added) {
          return (
            <span key={key} className="bg-green-500/20 text-green-700">
              {base}
            </span>
          );
        }

        if (part.removed) {
          return (
            <span
              key={key}
              className={variant === 'before' ? 'bg-red-500/20 text-red-700' : 'opacity-40'}
            >
              {base}
            </span>
          );
        }

        return (
          <span key={key} className="text-muted-foreground">
            {base}
          </span>
        );
      })}
      "
    </span>
  );
};

export const renderDiffValue = (
  field: string,
  value: unknown,
  counterpart: unknown,
  variant: 'before' | 'after',
): ReactNode => {
  const beforeRaw = variant === 'before' ? value : counterpart;
  const afterRaw = variant === 'before' ? counterpart : value;
  const displayValue = variant === 'before' ? beforeRaw : afterRaw;

  if (variant === 'before' && (beforeRaw === null || beforeRaw === undefined)) {
    return <span className="text-muted-foreground">None</span>;
  }
  if (variant === 'after' && (afterRaw === null || afterRaw === undefined)) {
    return <span className="text-muted-foreground">None</span>;
  }

  if (TEXT_DIFF_FIELDS.has(field)) {
    const beforeText =
      typeof beforeRaw === 'string'
        ? beforeRaw
        : beforeRaw === null || beforeRaw === undefined
        ? ''
        : String(beforeRaw);
    const afterText =
      typeof afterRaw === 'string'
        ? afterRaw
        : afterRaw === null || afterRaw === undefined
        ? ''
        : String(afterRaw);
    return renderStringDiff(beforeText, afterText, variant);
  }

  if (field === 'question_type_id') {
    const renderLabel = (raw: unknown) => {
      if (typeof raw === 'string') {
        const normalized = raw.toLowerCase();
        return (
          QUESTION_TYPE_LABEL_BY_SLUG[normalized] ??
          normalized
            .replace(/[_\s]+/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase())
            .trim()
        );
      }

      if (typeof raw === 'number') {
        return `Type ${raw}`;
      }

      if (raw && typeof raw === 'object' && typeof (raw as { name?: string }).name === 'string') {
        return QUESTION_TYPE_LABEL_BY_SLUG[(raw as { name: string }).name] ?? (raw as { name: string }).name;
      }

      return 'Unknown';
    };

    return <span>{renderLabel(displayValue)}</span>;
  }

  if (field === 'is_active') {
    const boolValue = displayValue === true;
    return <span>{boolValue ? 'Active' : 'Inactive'}</span>;
  }

  if (field === 'citations') {
    const citationList = Array.isArray(displayValue) ? displayValue : [];
    if (citationList.length === 0) {
      return <span className="text-muted-foreground">None</span>;
    }
    return (
      <ol className="ml-4 list-decimal space-y-1 text-sm text-muted-foreground">
        {citationList.map((entry, index) => (
          <li key={`citation-${variant}-${index}`} className="leading-relaxed">
            {typeof entry === 'string' && entry.trim().length > 0 ? (
              entry
            ) : (
              <span className="italic text-muted-foreground/70">Empty</span>
            )}
          </li>
        ))}
      </ol>
    );
  }

  try {
    return (
      <span className="font-mono text-sm">
        {JSON.stringify(displayValue)}
      </span>
    );
  } catch {
    return <span>{String(displayValue)}</span>;
  }
};

export const summarizeDiff = (diffs: Array<{ field: string }>): ReactNode[] =>
  diffs.map((item) => FIELD_LABELS[item.field] ?? item.field);
